import 'typeface-roboto';
import 'bz-utilities/global';

import MomentUtils from '@date-io/moment';
import { CircularProgress, createTheme, MuiThemeProvider } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ClearAcceptProvider from 'bz-core-controls/controls/payments/clear-accept/provider';
import BzTheme from 'bz-core-controls/styles/theme';
import { Register } from 'bz-icons';
import { DictionaryStoreProvider } from 'bz-utilities/dictionary/dictionary-provider';
import MediatorStore from 'bz-utilities/modules/plugins/register/stores/mediator';
import MitelChat from 'connect/core/controls/chat/mitel';
import ConnectFeature from 'connect/core/controls/features-flags/feature';
import connectFeaturesStore from 'connect/core/controls/features-flags/feature-store';
import MobileDetect from 'mobile-detect';
import { computed, observable, runInAction } from 'mobx';
import { observer, Provider } from 'mobx-react';
import { AreYouSureProvider } from 'modules/controls/are-you-sure';
import React from 'react';
import { Toaster } from 'react-hot-toast';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import NewUX from 'shell/new-ux';
import { cache as coreCache } from 'tm3-core/cache';
import ModuleFeatureProvider from 'tm3-core/controls/module/provider';
import FeatureEnum from 'tm3-core/enums/feature-enum';
import cache from './cache';
import Manifest from './manifest';
import SquareWrapper from './modules/square-provider-wrapper';
import AuthStore, { AuthStoreContext } from './stores/auth-store';
import ProviderStore, { ProviderStoreContext } from './stores/provider-store';
import SearchStore, { SearchStoreCtx } from './stores/search-store';
import themes from './themes';
import './connect-fonts.css';
import './modules/book/next/theme-tokens.css';
import { syncConnectGoogleFonts } from './utils/connect-font-loader';

const SiteHidden = React.lazy(() => import('./modules/site-hidden'));
const Shell = React.lazy(() => import('./shell'));
const Print = React.lazy(() => import('./print'));

interface IProps {}

function hexToRgb(hex?: string): { r: number; g: number; b: number } | null {
  const value = (hex ?? '').trim();
  const normalised = /^#?[0-9a-f]{6}$/i.test(value)
    ? value.startsWith('#')
      ? value.slice(1)
      : value
    : /^#?[0-9a-f]{3}$/i.test(value)
      ? (value.startsWith('#') ? value.slice(1) : value)
          .split('')
          .map((c) => c + c)
          .join('')
      : null;

  if (!normalised) return null;

  return {
    r: parseInt(normalised.slice(0, 2), 16),
    g: parseInt(normalised.slice(2, 4), 16),
    b: parseInt(normalised.slice(4, 6), 16)
  };
}

function relativeLuminance(hex?: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 1;
  const channels = [rgb.r, rgb.g, rgb.b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function getElevatedTextVars(surfaceElevated?: string): {
  onElevated: string;
  onElevatedMuted: string;
  onElevatedSubtle: string;
  onElevatedBorder: string;
} {
  const isDark = relativeLuminance(surfaceElevated) < 0.35;
  if (!isDark) {
    return {
      onElevated: '#0F172A',
      onElevatedMuted: '#475569',
      onElevatedSubtle: '#94A3B8',
      onElevatedBorder: '#E2E8F0'
    };
  }

  return {
    onElevated: '#F8FAFC',
    onElevatedMuted: '#CBD5E1',
    onElevatedSubtle: '#94A3B8',
    onElevatedBorder: 'rgba(248,250,252,0.25)'
  };
}

@observer
class App extends React.Component {
  @observable isLoaded = false;
  @observable isProvider = false;
  queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: 1000 * 30 /* 30 seconds */ } }
  });
  mediator: MediatorStore;

  constructor(props: IProps) {
    super(props);

    cache.providerStore = new ProviderStore();
    cache.authStore = new AuthStore();
    cache.searchStore = new SearchStore();
    connectFeaturesStore.load();
    cache.searchStore.loadingPromise = cache.searchStore.load();

    this.mediator = new MediatorStore();
    coreCache.mediator = this.mediator;

    window.mobileDetection = new MobileDetect(window.navigator.userAgent);

    this.loadConnect();
  }

  render() {
    if (!this.isLoaded) {
      return (
        <div style={{ display: 'grid', margin: '25px', justifyItems: 'center' }}>
          <CircularProgress />
        </div>
      );
    }

    const { isSiteLive, connectEnabled } = cache.providerStore;

    let content;
    let manifest;
    if (!connectEnabled || !isSiteLive) {
      content = <SiteHidden />;
    } else {
      manifest = <Manifest />;
      content = (
        <Switch>
          <Route path="/print" component={Print} />
          <Route path="/" component={Shell} />
        </Switch>
      );
    }

    return (
      <MuiThemeProvider theme={this.theme}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <SearchStoreCtx.Provider value={cache.searchStore}>
            <QueryClientProvider client={this.queryClient}>
              <Provider
                authStore={cache.authStore}
                providerStore={cache.providerStore}
                searchStore={cache.searchStore}
                mediator={this.mediator}
              >
                <DictionaryStoreProvider appLocation={cache.providerStore.location}>
                  <div className="connect-font-theme" style={this.connectCssVars}>
                    <NewUX isConnect primary={this.theme.palette.primary.main}>
                      <AuthStoreContext.Provider value={cache.authStore}>
                        <ModuleFeatureProvider authenticated={true}>
                          <ProviderStoreContext.Provider value={cache.providerStore}>
                            <>
                              <ConnectFeature flag={FeatureEnum.FF_VHG_MITEL_CHAT}>
                                <MitelChat />
                              </ConnectFeature>
                              <SquareWrapper>
                                <ClearAcceptProvider>
                                  <AreYouSureProvider>
                                    <Register>
                                      <React.Fragment>
                                        <BzTheme />
                                        <Toaster
                                          position="top-right"
                                          toastOptions={{
                                            success: { duration: 6000 },
                                            error: { duration: 8000 }
                                          }}
                                        />
                                        {manifest}
                                        <Router>
                                          <React.Suspense
                                            fallback={
                                              <div>
                                                <CircularProgress />
                                              </div>
                                            }
                                          >
                                            {content}
                                          </React.Suspense>
                                        </Router>
                                      </React.Fragment>
                                    </Register>
                                  </AreYouSureProvider>
                                </ClearAcceptProvider>
                              </SquareWrapper>
                            </>
                          </ProviderStoreContext.Provider>
                        </ModuleFeatureProvider>
                      </AuthStoreContext.Provider>
                    </NewUX>
                  </div>
                </DictionaryStoreProvider>
              </Provider>
            </QueryClientProvider>
          </SearchStoreCtx.Provider>
        </MuiPickersUtilsProvider>
      </MuiThemeProvider>
    );
  }

  componentDidMount() {
    this.syncFonts();
  }

  componentDidUpdate() {
    this.syncFonts();
  }

  private syncFonts() {
    const bodyFont = cache.providerStore.connectFontBodyFamily || 'Nunito Sans';
    const headingFont = cache.providerStore.connectFontHeadingFamily || 'Nunito Sans';

    syncConnectGoogleFonts([bodyFont, headingFont], 'connect-google-fonts-runtime');

    if (typeof document !== 'undefined') {
      document.body.classList.add('connect-font-theme');
      document.body.classList.add('connect-next-theme');
      Object.entries(this.connectCssVars).forEach(([key, value]) => {
        if (typeof value === 'string') {
          document.documentElement.style.setProperty(key, value);
          document.body.style.setProperty(key, value);
        }
      });
    }
  }

  private async loadConnect() {
    const params = window.location.search.split('?');
    const embedded = params.includes('embedded');
    try {
      await cache.providerStore.onLoad(embedded);
      runInAction(() => {
        this.isLoaded = true;
        this.isProvider = true;
        cache.authStore.checkUser();
      });
    } catch (err) {
      this.isLoaded = true;
    }
  }

  @computed
  get connectCssVars(): React.CSSProperties {
    const elevated = getElevatedTextVars(cache.providerStore.connectThemeSurfaceElevated);

    return {
      ['--connect-font-body' as any]: cache.providerStore.connectFontBodyFamily || 'Nunito Sans',
      ['--connect-font-heading' as any]:
        cache.providerStore.connectFontHeadingFamily || 'Nunito Sans',
      ['--connect-surface' as any]: cache.providerStore.connectThemeSurface,
      ['--connect-surface-muted' as any]: cache.providerStore.connectThemeSurfaceMuted,
      ['--connect-surface-subtle' as any]: cache.providerStore.connectThemeSurfaceSubtle,
      ['--connect-surface-elevated' as any]: cache.providerStore.connectThemeSurfaceElevated,
      ['--connect-text-default' as any]: cache.providerStore.connectThemeTextDefault,
      ['--connect-text-muted' as any]: cache.providerStore.connectThemeTextMuted,
      ['--connect-text-subtle' as any]: cache.providerStore.connectThemeTextSubtle,
      ['--connect-border-default' as any]: cache.providerStore.connectThemeBorderDefault,
      ['--connect-border-muted' as any]: cache.providerStore.connectThemeBorderMuted,
      ['--connect-focus-ring' as any]: cache.providerStore.connectThemeFocusRing,
      ['--connect-primary' as any]: cache.providerStore.customColourPrimary,
      ['--connect-primary-faded' as any]: cache.providerStore.connectThemeSurfaceSubtle,
      ['--connect-status-success' as any]: cache.providerStore.connectThemeStatusSuccess,
      ['--connect-status-success-surface' as any]:
        cache.providerStore.connectThemeStatusSuccessSurface,
      ['--connect-status-warning' as any]: cache.providerStore.connectThemeStatusWarning,
      ['--connect-status-warning-surface' as any]:
        cache.providerStore.connectThemeStatusWarningSurface,
      ['--connect-status-critical' as any]: cache.providerStore.connectThemeStatusCritical,
      ['--connect-status-critical-surface' as any]:
        cache.providerStore.connectThemeStatusCriticalSurface,
      ['--connect-on-elevated' as any]: elevated.onElevated,
      ['--connect-on-elevated-muted' as any]: elevated.onElevatedMuted,
      ['--connect-on-elevated-subtle' as any]: elevated.onElevatedSubtle,
      ['--connect-on-elevated-border' as any]: elevated.onElevatedBorder
    };
  }

  @computed
  get theme() {
    const { skin = 'default' } = cache.providerStore;
    const theme = themes[skin];

    theme.overrides = {
      MuiFormControl: {
        root: {
          // marginRight: '15px',
          flexGrow: '2'
        }
      },
      MuiTab: {
        root: {
          fontWeight: 700,
          '@media(min-width: 960px)': {
            minWidth: '5px'
          }
        }
      },

      MuiButton: {
        root: {
          fontWeight: 600
        }
      },
      MuiAppBar: {
        root: {
          boxShadow: 'none'
        }
      }
    };

    const bodyFont = cache.providerStore.connectFontBodyFamily || 'Nunito Sans';
    const headingFont = cache.providerStore.connectFontHeadingFamily || 'Nunito Sans';

    theme.typography = {
      fontFamily: `${bodyFont}, sans-serif`,
      htmlFontSize: 14,
      fontSize: 13,
      useNextVariants: true,
      h1: { fontFamily: `${headingFont}, sans-serif` },
      h2: { fontFamily: `${headingFont}, sans-serif` },
      h3: { fontFamily: `${headingFont}, sans-serif` },
      h4: { fontFamily: `${headingFont}, sans-serif` },
      h5: { fontFamily: `${headingFont}, sans-serif` },
      h6: { fontFamily: `${headingFont}, sans-serif` }
    };

    theme.primary = cache.providerStore.customColourPrimary || theme['primary'];
    theme.secondary = cache.providerStore.customColourSecondary || theme['secondary'];
    theme.nav = cache.providerStore.customColourNav || theme['nav'];
    theme.navText = cache.providerStore.customColourNavText || theme['navText'];

    theme.palette = {
      // using '#fff' instead of 'white' prevents date pickers from crashing in register form
      primary: { main: theme.primary || themes['default'].primary },
      secondary: { main: theme.secondary || themes['default'].secondary },
      nav: {
        main: theme.nav || themes['default'].nav,
        contrastText: theme.navText || themes['default'].navText
      },
      error: { main: '#dc3545' },
      tonalOffset: 0.2
    };

    return createTheme(theme);
  }
}

export default App;
